/**
 * VICIN GameBoy Edition — root app.
 * Renders the GameBoy shell and routes between screens.
 *
 * Navigation model: the shell dispatches custom DOM events for each
 * hardware button. Each screen subscribes to the events it needs
 * (D-pad, A, B, SELECT, START). Keyboard is mapped to the same events
 * so desktop users can play too.
 *
 * PWA-only mode: pwa.vicin.app is meant to run as an INSTALLED PWA
 * (added to home screen). If a user opens it in a regular browser tab,
 * we show an InstallPrompt screen instead of the GameBoy shell — so
 * they understand the app is meant to be installed, not browsed.
 * Use ?pwa-bypass=1 (or ?bypass-pwa=1) in the URL to skip the prompt
 * for development / desktop testing.
 */
import { useEffect, useState } from 'react';
import { useApp } from './state/store';
import { GameBoyShell } from './shell/GameBoyShell';
import { BootScreen } from './screens/BootScreen';
import { AuthScreen } from './screens/AuthScreen';
import { HomeScreen } from './screens/HomeScreen';
import { DoorScreen } from './screens/DoorScreen';
import { DoorbellScreen } from './screens/DoorbellScreen';
import { DoorbellShareScreen } from './screens/DoorbellShareScreen';
import { VoicemailScreen } from './screens/VoicemailScreen';
import { AnnouncementsScreen } from './screens/AnnouncementsScreen';
import { IncidentsScreen } from './screens/IncidentsScreen';
import { VotesScreen } from './screens/VotesScreen';
import { FeesScreen } from './screens/FeesScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { AdminScreen } from './screens/AdminScreen';
import { DetailScreen } from './screens/DetailScreen';
import { InstallPrompt } from './screens/InstallPrompt';
import { isRunningAsPwa, hasPwaBypass } from './lib/pwaDetect';
import { ModalScreen } from './screens/ModalScreen';
import { ChatScreen } from './screens/ChatScreen';
import { sfx, unlockAudio } from './lib/audio';

function dispatch(event: string) {
  window.dispatchEvent(new CustomEvent(`vicin-gb:${event}`));
}

export default function App() {
  const { screen, isAuthenticated, goto, toast, hideToast } = useApp();
  const [showInstall, setShowInstall] = useState<boolean | null>(null);

  // PWA-only gate. If the page is opened as a regular browser tab on the
  // production domain (not installed PWA), show InstallPrompt instead
  // of the GameBoy shell. URL flag ?pwa-bypass=1 skips this for
  // development / desktop testing.
  useEffect(() => {
    let cancelled = false;
    import('./lib/pwaDetect').then(({ shouldShowInstallPrompt }) => {
      if (!cancelled) setShowInstall(shouldShowInstallPrompt());
    });
    return () => { cancelled = true; };
  }, []);

  // Unlock audio context on first user gesture (browsers require this).
  useEffect(() => {
    const unlock = () => { unlockAudio(); window.removeEventListener('pointerdown', unlock); };
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  // Foreground push listener — when the SW receives a push and posts a
  // message to clients (because the app is in foreground), play the
  // doorbell chime locally. iOS suppresses the system push sound when
  // the app is open, so the in-app audio is the only sound the user
  // hears.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'open_chat' && data.ring_id) {
        // Notification tapped while app was foregrounded — open chat
        // directly so the resident can reply without bouncing through
        // the doorbell screen.
        useApp.getState().openChat(String(data.ring_id));
        return;
      }
      if (data.type !== 'push-received') return;
      if (data.kind === 'doorbell') {
        sfx.doorbell();
        // Auto-navigate to doorbell screen if user is on home/door so
        // they don't miss the ring. Don't yank them from announcements
        // or any detail screen they're actively reading.
        const cur = useApp.getState().screen;
        if (cur === 'home' || cur === 'door') {
          useApp.getState().goto('doorbell');
        }
      } else if (data.kind === 'voicemail') {
        // Voicemails are less urgent than doorbells — don't play a
        // chime (it'd be obnoxious for someone who gets many VMs).
        // Just navigate to the voicemails screen from home/door so
        // the user can review at their own pace.
        const cur = useApp.getState().screen;
        if (cur === 'home' || cur === 'door') {
          useApp.getState().goto('voicemails');
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [goto]);

  // Keyboard → hardware buttons
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const inField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (inField) {
        if (e.key === 'Escape') (t as HTMLElement).blur();
        return;
      }
      switch (e.key) {
        case 'ArrowUp':    e.preventDefault(); dispatch('up'); break;
        case 'ArrowDown':  e.preventDefault(); dispatch('down'); break;
        case 'ArrowLeft':  e.preventDefault(); dispatch('left'); break;
        case 'ArrowRight': e.preventDefault(); dispatch('right'); break;
        case 'a': case 'A': case 'z': case 'Z': case 'Enter': case ' ':
          e.preventDefault(); dispatch('a'); break;
        case 'b': case 'B': case 'x': case 'X': case 'Escape': case 'Backspace':
          e.preventDefault(); dispatch('b'); break;
        case 'Tab':
          e.preventDefault(); dispatch('select'); break;
        case 's': case 'S':
          e.preventDefault(); dispatch('start'); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Session expired → go to auth
  useEffect(() => {
    const handler = () => {
      sfx.error();
      if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
      goto('auth');
    };
    window.addEventListener('vicin-gb:session-expired', handler);
    return () => window.removeEventListener('vicin-gb:session-expired', handler);
  }, [goto]);

  // Auto-dismiss toast — errores se quedan 4s, info/success 5s para dar
  // tiempo a leer (importante para el código dev en login)
  useEffect(() => {
    if (!toast) return;
    const ms = toast.kind === 'error' ? 4000 : 5000;
    const id = setTimeout(hideToast, ms);
    return () => clearTimeout(id);
  }, [toast, hideToast]);

  const handleStart = () => {
    sfx.confirm();
    goto(isAuthenticated ? 'home' : 'auth');
  };

  const currentScreen = (() => {
    switch (screen) {
      case 'boot': return <BootScreen onStart={handleStart} />;
      case 'auth': return <AuthScreen />;
      case 'home': return <HomeScreen />;
      case 'door': return <DoorScreen />;
      case 'doorbell': return <DoorbellScreen />;
      case 'doorbell-share': return <DoorbellShareScreen />;
      case 'voicemails': return <VoicemailScreen />;
      case 'announcements': return <AnnouncementsScreen />;
      case 'incidents': return <IncidentsScreen />;
      case 'votes': return <VotesScreen />;
      case 'fees': return <FeesScreen />;
      case 'profile': return <ProfileScreen />;
      case 'settings': return <SettingsScreen />;
      case 'admin': return <AdminScreen />;
      case 'detail': return <DetailScreen />;
      case 'modal': return <ModalScreen />;
      case 'chat': return <ChatScreen />;
      default: return <BootScreen onStart={handleStart} />;
    }
  })();

  // While we're checking PWA state, render nothing (avoids flash of shell).
  if (showInstall === null) return null;

  // Not a PWA + no bypass → install prompt (no shell, just the LCD).
  if (showInstall) {
    return <InstallPrompt />;
  }

  return (
    <>
      <GameBoyShell
        onUp={() => dispatch('up')}
        onDown={() => dispatch('down')}
        onLeft={() => dispatch('left')}
        onRight={() => dispatch('right')}
        onA={() => dispatch('a')}
        onB={() => dispatch('b')}
        onSelect={() => dispatch('select')}
        onStart={() => dispatch('start')}
        showControls={screen !== 'boot'}
      >
        {currentScreen}
        {toast && (
          <div
            className={`gb-toast gb-toast--${toast.kind}`}
            role="status"
            data-testid={`toast-${toast.kind}`}
          >
            <span className="gb-toast__icon" aria-hidden="true">
              {toast.kind === 'error' ? '!' : toast.kind === 'success' ? '✓' : '►'}
            </span>
            <span className="gb-toast__text">{toast.text}</span>
          </div>
        )}
      </GameBoyShell>
    </>
  );
}
